import { countConditions, type Filter } from "@estelle/domain";
import { ChevronDown, SlidersHorizontal } from "lucide-react";
import { useId, useState, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * A report's list toolbar behind one "Filters" button (#639).
 *
 * Reports › Orders narrows its chart with the same filter builder the Orders
 * list uses, but a search box and a saved-view menu above a chart read as a
 * list that failed to load. Closed, the row says how many filters apply; open,
 * it is the shared toolbar, unchanged. A link that carries a filter opens with
 * the bar open, so what narrows the chart is never hidden.
 *
 * Off (`enabled` false) it renders the toolbar as it was, so a list section
 * passes straight through.
 */
export function CollapsedFilters({
  enabled,
  filter,
  search,
  children,
}: {
  enabled: boolean;
  filter: Filter;
  search: string | undefined;
  children: ReactNode;
}) {
  const applied =
    countConditions(filter) + ((search ?? "").trim() === "" ? 0 : 1);
  const [open, setOpen] = useState(applied > 0);
  const panelId = useId();

  if (!enabled) return <>{children}</>;

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => {
            setOpen((was) => !was);
          }}
        >
          <SlidersHorizontal />
          Filters
          {applied > 0 ? (
            <span className="bg-foreground text-background rounded-sm px-1.5 text-xs tabular-nums">
              {applied}
            </span>
          ) : null}
          <ChevronDown
            className={cn("transition-transform", open && "rotate-180")}
          />
        </Button>
        {!open ? (
          <span className="text-muted-foreground text-xs">
            {applied === 0
              ? "Every order counts."
              : `${String(applied)} ${applied === 1 ? "filter narrows" : "filters narrow"} the chart.`}
          </span>
        ) : null}
      </div>
      <div id={panelId} hidden={!open}>
        {children}
      </div>
    </div>
  );
}
